/**
 * What KIND of word a grammar-lesson vocab card teaches, and the question to ask a child about it.
 *
 * Universal on purpose. The server needs it to plan each vocab turn; the mobile app needs it to speak
 * the session's opening line, which happens BEFORE any round-trip and so cannot ask the server. That
 * used to mean the mobile hand-mirrored the rules with a "keep in sync" comment — which drifted
 * immediately: its adjective set was missing `straight`, `curly`, `dotted`, `zigzag`, `long`, `short`,
 * `tall`, `new`, `old`, `hot`, `cold` and `fast`, all of which appear in real lesson decks, so a lines
 * lesson opened by asking "What is this?" of a `dotted` card. One implementation, imported twice, is
 * the only thing that actually prevents that.
 *
 * Pure string logic — no DOM, no React Native, no Node APIs — per the dependency decision tree in
 * CLAUDE.md.
 */

import type { ConversationMetaEntry, WordCategory } from "@ai-teach/types";

// The RIGHT vocab question per semantic category — not a "What ${category} is
// this?" template (which reads wrong for job/family/place/sound). Categories
// without an entry here fall through to "What is this?".
const CATEGORY_QUESTION: Partial<Record<WordCategory, string>> = {
  animal: "What animal is this?",
  food: "What food is this?",
  drink: "What drink is this?",
  number: "What number is this?",
  color: "What color is it?",
  family: "Who is this?",
  job: "Who is this?",
  place: "Where is this?",
  action: "What action is this?",
  expression: "What does this say?",
  // `sound` relies on the classifier's elicitationQuestion ("What does a chicken
  // say?"); `other` is unsupported — both reserve the generic "What is this?".
};

/** Number words a card can teach, and the digits speech-to-text returns for them. */
export const NUMBER_WORDS = new Set([
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
  "thirteen",
  "fourteen",
  "fifteen",
  "sixteen",
  "seventeen",
  "eighteen",
  "nineteen",
  "twenty",
]);

export const COLOR_WORDS = new Set([
  "red",
  "blue",
  "green",
  "yellow",
  "orange",
  "pink",
  "purple",
  "black",
  "white",
  "brown",
  "gray",
  "grey",
]);

/** Describing words a card can teach — a colour, or another attribute. Never an object to identify. */
export const ATTRIBUTE_ADJECTIVES = new Set([
  ...COLOR_WORDS,
  "straight",
  "curly",
  "dotted",
  "zigzag",
  "big",
  "small",
  "long",
  "short",
  "tall",
  "new",
  "old",
  "hot",
  "cold",
  "fast",
  "slow",
  "round",
]);

/** Lowercase, strip punctuation, collapse whitespace — the shared comparison form for a card word. */
export function normalizeWord(text: string | null | undefined): string {
  return (text ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .trim()
    .replace(/\s+/g, " ");
}

/** Greetings, feelings and politeness formulas — a card to READ ("What does this say?"), never an
 * object to identify. Bounded on purpose; stored normalized so "you're welcome" matches "youre welcome". */
export const EXPRESSION_WORDS = new Set(
  [
    "hello",
    "hi",
    "goodbye",
    "bye",
    "thank you",
    "thanks",
    "nice to meet you",
    "see you later",
    "good morning",
    "good afternoon",
    "good evening",
    "fine",
    "great",
    "good",
    "sorry",
    "excuse me",
    "you're welcome",
  ].map(normalizeWord)
);

/** Deck verbs a card can teach as an ACTION, for the lessons whose focus never puts them in a modal
 * slot ("What can you do?" listed as separate cards). Bounded — deck nouns (bear/lion/monkey) are NOT
 * here so they stay objects to identify. */
export const ACTION_WORDS = new Set(
  [
    "sing",
    "dance",
    "jump",
    "climb",
    "draw",
    "swim",
    "fly",
    "run",
    "walk",
    "hop",
    "skip",
    "clap",
    "bow",
    "hug",
    "kiss",
    "shake hands",
    "ballet",
    "tap dance",
    "belly dance",
    "hip hop",
  ].map(normalizeWord)
);

/** Antonym pairs for a non-colour attribute, so its vocab question becomes a two-choice contrast
 * ("Is it big or small?") the child can answer by NAMING a word. Every key must be in
 * ATTRIBUTE_ADJECTIVES to be reachable. Round-tripping pairs are symmetric; `tall` is a one-way extra
 * (its antonym is `short`, whose own default antonym stays `long`). */
const CONTRAST_MAP: Record<string, string> = {
  big: "small",
  small: "big",
  long: "short",
  short: "long",
  tall: "short",
  straight: "curly",
  curly: "straight",
  fast: "slow",
  slow: "fast",
  hot: "cold",
  cold: "hot",
  new: "old",
  old: "new",
};

/**
 * Is this card word an ACTION in THIS lesson? Decided by grammatical position rather than a verb list:
 * an action is the slot that follows the modal "can".
 *
 * Position is what matters, because the subject of an inverted question follows it too. In
 * "Can {{a lion}} {{fly}}?" both slots come after "can" and only the LAST is the verb — a line-level
 * test would classify the lion as an action and ask a child "What action is this?" over a picture of one.
 */
export function isActionWord(
  word: string | null | undefined,
  grammarFocus: string | null | undefined
): boolean {
  const w = normalizeWord(word);
  if (!w) return false;
  if (ACTION_WORDS.has(w)) return true; // curated deck verb — action wherever it appears
  for (const line of (grammarFocus ?? "").split("\n")) {
    const afterModal = line.match(/\bcan\b(.*)$/i)?.[1];
    if (afterModal) {
      const slots = [...afterModal.matchAll(/\{\{(.*?)\}\}/g)].map((s) => normalizeWord(s[1]));
      if (slots.length > 0 && slots[slots.length - 1] === w) return true;
    }
    // "Let's {{jump}}!" / "Let's shake hands." — the action sits IMMEDIATELY after "let's" (no inverted
    // subject can come between, unlike "can"), so ONLY the first token right after it is the verb. A later
    // slot is an object, not the action ("Let's go to the {{park}}!" → park is a noun).
    const afterLets = line.match(/\blet'?s\b(.*)$/i)?.[1];
    if (afterLets !== undefined) {
      const rest = afterLets.trimStart();
      const adjacentSlot = rest.match(/^\{\{(.*?)\}\}/);
      if (adjacentSlot) {
        if (normalizeWord(adjacentSlot[1]) === w) return true;
      } else {
        const literal = normalizeWord(rest);
        if (literal === w || literal.startsWith(`${w} `)) return true;
      }
    }
  }
  return false;
}

export type VocabWordKind = "noun" | "adjective" | "number" | "phrase" | "action" | "expression";

export function vocabWordKindOf(
  word: string | null | undefined,
  grammarFocus?: string | null
): VocabWordKind {
  const raw = (word ?? "").trim();
  const w = normalizeWord(raw);
  if (!w) return "noun";
  // Action wins over the whitespace rule below — "tap dance" is an action, not a phrase to read out.
  if (isActionWord(raw, grammarFocus)) return "action";
  if (/^\d+$/.test(w) || NUMBER_WORDS.has(w)) return "number";
  if (ATTRIBUTE_ADJECTIVES.has(w)) return "adjective";
  // Expressions win over the whitespace→phrase rule so a multi-word greeting ("good morning",
  // "thank you") classifies as an expression, not a bare phrase.
  if (EXPRESSION_WORDS.has(w)) return "expression"; // greeting / feeling (hello, fine, thank you)
  if (/\s/.test(raw)) return "phrase"; // "seaweed soup" — read it, don't identify it
  return "noun";
}

/**
 * The vocab-phase question for a card. Everything used to get "What is it?" unless the card was a
 * number or a colour, so an actions lesson asked it of a `sing` card and a greetings lesson of a
 * `hello` card. `deckWords` (when given) lets a non-colour attribute ask a two-choice contrast
 * against a sibling card ("Is it big or small?") instead of a bare colour question.
 */
export function vocabQuestionForWord(
  word: string | null | undefined,
  grammarFocus?: string | null,
  deckWords?: string[],
  // The precomputed conversationMeta entry for this word, when available —
  // its elicitationQuestion (validated) wins; else its category picks the
  // question from the phrase map. Absent → the word-kind heuristic below.
  entry?: ConversationMetaEntry | null
): string {
  const elicit = entry?.elicitationQuestion?.trim();
  if (elicit) return elicit;
  if (entry?.category) {
    const byCategory = CATEGORY_QUESTION[entry.category];
    if (byCategory) return byCategory;
    return "What is this?"; // supported metadata, unsupported category (sound/other) → generic
  }
  switch (vocabWordKindOf(word, grammarFocus)) {
    case "action":
      return "What action is this?";
    case "number":
      return "What number is this?";
    case "adjective":
      return attributeQuestion(word, deckWords);
    case "phrase":
    case "expression":
      return "What does this say?";
    default:
      // A picture/noun with no metadata: "What is this?" — never "What word is
      // this?" (that reads wrong for a picture, and confused a farm-sound card).
      return "What is this?";
  }
}

/** A colour card asks its colour; a non-colour attribute asks a two-choice contrast against a deck
 * sibling, falling back to reading it when the deck offers no foil. */
function attributeQuestion(word: string | null | undefined, deckWords?: string[]): string {
  const w = normalizeWord(word);
  if (COLOR_WORDS.has(w)) return "What color is it?";
  const foil = pickContrastFoil(w, deckWords);
  return foil ? `Is it ${w} or ${foil}?` : "What does this say?";
}

/** The foil for a two-choice attribute question: a mapped antonym that is in the deck, else the first
 * OTHER non-colour attribute in deck order, else null (no meaningful contrast to draw). */
function pickContrastFoil(w: string, deckWords?: string[]): string | null {
  const deckNorm = (deckWords ?? []).map(normalizeWord).filter(Boolean);
  const antonym = CONTRAST_MAP[w];
  if (antonym && deckNorm.includes(antonym)) return antonym;
  for (const d of deckNorm) {
    if (d !== w && ATTRIBUTE_ADJECTIVES.has(d) && !COLOR_WORDS.has(d)) return d;
  }
  return null;
}

const FIRST_PERSON_SPEECH_RE = /^\s*(i|i'?m|my)\b/i;

/**
 * Is this grammar_focus a MINI-SPEECH — an all-statements personal monologue the child delivers ("My
 * name is _. I am _ years old.") rather than a picture/dialogue drill? The ONE implementation, imported
 * by BOTH the API parser (which sets `mode:"mini_speech"`) and the mobile opener (which must NOT run a
 * vocab warm-up over it), so the two can never drift. Mirrors the parser's line handling exactly: split
 * on newlines, read `A:`/`B:` labels, a line is a question if it has a "?". Two authoring shapes count —
 * UNLABELED all-statements, OR correctly-labeled all-`B` first-person statements.
 */
export function isMiniSpeechFocus(grammarFocus: string | null | undefined): boolean {
  const lines = (grammarFocus ?? "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return false;
  let hasAnyLabel = false;
  const steps = lines.map((line) => {
    const m = line.match(/^([ABab])\s*:\s*(.*)$/);
    if (m?.[1]) hasAnyLabel = true;
    const speaker = m?.[1] ? (m[1].toUpperCase() as "A" | "B") : "A";
    const text = m ? (m[2] ?? "") : line;
    return { speaker, text, isQuestion: text.includes("?") };
  });
  const noQuestions = steps.every((s) => !s.isQuestion);
  const labeledFirstPersonSpeech =
    hasAnyLabel && steps.every((s) => s.speaker === "B" && FIRST_PERSON_SPEECH_RE.test(s.text));
  return noQuestions && (!hasAnyLabel || labeledFirstPersonSpeech);
}

/**
 * The ONE warm-up decision the mobile opener and the server share: run the vocab warm-up only when
 * there are picture cards to name AND the slide isn't a mini-speech monologue (which opens with its
 * first speech line, not a "name the card" prompt). Kept in lockstep with the parser via the shared
 * `isMiniSpeechFocus` (see the load-bearing agreement test).
 */
export function shouldRunVocabWarmup(input: {
  grammarFocus?: string | null | undefined;
  deck: readonly unknown[];
}): boolean {
  return input.deck.length > 0 && !isMiniSpeechFocus(input.grammarFocus);
}
